/*
 * @Description : store 入口
 * @Author      : pishengjun
 * @Date        : 2022-06-08 14:47:16
 */

import type { InjectionKey } from "vue";
import { createStore, useStore as baseUseStore, Store, type Payload } from "vuex";
import { storage, session_storage } from "@/utils/storage";
import { nameSpace } from "./interface";
import { ExamModule } from "./modules/exam";
import autoLoginPlugin from "./plugins/auto-login.plugin";
import type { IRootStore, ICompleteStore, Payloads, PayloadExamMap } from "@/store/interface";

const namePrefix = nameSpace.index + "-";
const getStorage = (name: string) => storage.get(namePrefix + name);
const options: any = {
  state: () => ({
    loading: getStorage("loading") ?? false,
    registerUserForm: getStorage("registerUserForm") ?? null,
    loginRegistrationSetting: getStorage("loginRegistrationSetting") ?? null,
    token: getStorage("token") ?? null,
    currentRegisterUser: getStorage("currentRegisterUser") ?? {},
    signalRHelper: getStorage("signalRHelper") ?? null,
    hiddenChangePassword: getStorage("hiddenChangePassword") ?? false,
    hiddenChangeAuthLevel: getStorage("hiddenChangeAuthLevel")?? false,
    isLogin: getStorage("isLogin") ?? false,
    webSystemSetting: getStorage("webSystemSetting") || {},
    // 用户操作的考试集合，用于存储用户操作的考试信息，键为考试ID，值为用户操作信息
    userOperationExamMap: getStorage("userOperationExamMap") || {},
    userOperationID: getStorage("userOperationID") || null, // 用户操作的考试ID
    examListIds: getStorage("examListIds") || [],

    message: {
      unreadCount: 0,
    },
    // 是否超过时间差
    isTimeDifferenceExcessive: false,
    // 显示时间差提示
    isPopupDisplayed: false,

    // 是否校验表单（新增，暂存表单时不校验表单，后续表单重构可删除）
    isValidateForm: false,

    // 登录成功后回调的地址
    // callbackUrl: getStorage("callbackUrl") || null,
    // 是否需要初始化OIDC
    needInitOidc: true,
    upgradeData: {},
    payOrderNo: getStorage("payOrderNo") || null, // 支付订单号,
  }),
  mutations: {
    /**
     * SET_DATA 突变函数，用于更新 store 中的状态，并可选择将数据存储到本地。
     * @param state - 根状态对象，类型为 IRootStore。
     * @param payload - 包含要更新的属性名、数据和是否存储到本地的标志。
     * @typeParam T - IRootStore 的键名类型。
     */
    SET_DATA<T extends keyof IRootStore>(state: IRootStore, payload: Payloads<T>) {
      // 解构赋值获取 payload 中的属性
      const { name, data, saveStorage = true } = payload;
      // 更新 state 中的对应属性
      state[name] = data;

      if (saveStorage) {
        try {
          // 尝试将数据存储到本地
          storage.set(namePrefix + name, data);
        } catch (error) {
          // 捕获并打印存储过程中可能出现的错误
          console.error(`Failed to save ${name} to storage:`, error);
        }
      }
    },
    // 用户操作的考试集合
    SET_USER_OPERATION_EXAMMAP<T extends keyof IRootStore>(state: IRootStore, payload: PayloadExamMap<T>) {
      // 解构赋值获取 payload 中的属性
      const { tenantId, data } = payload;
      if (!tenantId) return;
      // 确保 state.userOperationExamMap 是对象类型
      const currentExamMap = typeof state.userOperationExamMap === "object" && state.userOperationExamMap !== null ? state.userOperationExamMap : {};
      // 确保 data 是对象类型
      const validData = typeof data === "object" && data !== null ? data : {};
      // 更新 state 中的对应属性
      const newData = {
        ...currentExamMap,
        [tenantId]: {
          ...(currentExamMap[tenantId] || {}),
          ...validData,
        },
      };
      state.userOperationExamMap = newData;

      try {
        // 尝试将数据存储到本地
        storage.set(namePrefix + "userOperationExamMap", newData);
      } catch (error) {
        // 捕获并打印存储过程中可能出现的错误
        console.error(error);
      }
    },
    HIDE_LOADING(state) {
      state.loading = false;
    },
    SHOW_LOADING(state) {
      state.loading = true;
    },
    SET_UNREADR_COUNT(state, payload) {
      state.message.unreadCount = payload.count;
    },
    SET_isTimeDifferenceExcessive(state, payload) {
      state.isTimeDifferenceExcessive = payload;
    },
    SET_isPopupDisplayed(state, payload) {
      state.isPopupDisplayed = payload;
    },
  },
  getters: {
    /**
     * GET_DATA getter 函数，用于从 store 的 state 中获取指定名称的数据。
     * @param state - 根状态对象，类型为 IRootStore。
     * @returns 一个函数，该函数接收一个 IRootStore 的键名，返回对应的值。
     */
    GET_DATA: (state: IRootStore) => {
      return <T extends keyof IRootStore>(name: T): IRootStore[T] => {
        return state[name];
      };
    },
  },
  actions: {},
  modules: { exam: ExamModule },
};
// 创建store
export const store = createStore<IRootStore>({ ...options, plugins: [autoLoginPlugin] });

// 重置store
export function resetStore() {
  store.replaceState(createStore<IRootStore>(options).state);
}

// 清空所有store和缓存数据
export function clearStore() {
  storage.clear();
  session_storage.clear();
  resetStore();
}
/**
 * 清除 storage 和 session_storage 中包含指定键名的键值对。
 *
 * @param includeKeys 需要清除的键名数组。
 * 清除操作将基于键名前缀（如 `namePrefix`）来执行。
 * 如果没有提供 includeKeys 参数，则清除所有键值对。
 */
export function clearStoreIncludeKeys(includeKeys: string[] = []) {
  const keyNames = includeKeys.map((item) => namePrefix + item);
  storage.clearIncludeKeys(keyNames);
  session_storage.clearIncludeKeys(keyNames);
  resetStore();
}

/**
 * 清除 storage 和 session_storage 中除指定键名之外的所有键值对。
 *
 * @param excludeKeys 需要保留的键名数组。
 * 清除操作将基于键名前缀（如 `namePrefix`）来执行。
 * 如果没有提供 excludeKeys 参数，则清除所有键值对。
 */
export function clearStoreExcludeKeys(excludeKeys: string[] = []) {
  const keyNames = excludeKeys.map((item) => namePrefix + item);
  storage.clearExcludeKeys(keyNames);
  session_storage.clearExcludeKeys(keyNames);
  resetStore();
}

/**
 * 清除所有存储在 Cookie 中的数据。
 */
export function clearStoreCookie() {
  storage.clearCookie();
}

/**
 * 使用 Symbol() 创建一个注入键，用于标识根存储。
 * 该键用于在 Vue 应用程序中注入 Store 实例。
 */
export const key: InjectionKey<Store<IRootStore>> = Symbol();

/**
 * 使用该函数获取 Store 实例。
 *
 * @param <T> 泛型参数，表示 Store 的类型。
 *          默认为 `ICompleteStore`，即具有所有默认插件的完整 Store。
 *
 * @returns 返回一个 Store 实例。
 *
 * 示例：
 *
 * ```typescript
 * const store = useStore();
 * ```
 */
export function useStore<T = ICompleteStore>() {
  return baseUseStore<T>(key);
}
